import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleGoogle = () => {
    const redirectUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Missing fields', { description: 'Please enter both email and password.' });
      return;
    }
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success('Welcome back!');
      navigate(user.has_profile ? '/browse' : '/profile/setup');
    } catch (err) {
      toast.error('Login failed', { description: err.response?.data?.detail || 'Invalid credentials.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-fnl-dark relative overflow-hidden flex items-center justify-center px-4">
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-0 w-96 h-96 bg-fnl-pink rounded-full blur-3xl opacity-20"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-700 rounded-full blur-3xl opacity-20"></div>
      </div>

      <div className="relative z-10 w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 justify-center mb-8">
          <div className="relative">
            <span className="text-white font-bold text-3xl">F</span>
            <Heart className="absolute -top-1 -right-3 w-4 h-4 fill-fnl-pink text-fnl-pink" />
          </div>
          <span className="text-white font-semibold text-lg">Flirty</span>
        </Link>

        <div className="bg-fnl-purple/80 backdrop-blur rounded-2xl p-8 border border-purple-900/50">
          <h2 className="text-2xl font-bold text-white mb-6 text-center">Welcome Back</h2>

          <button
            onClick={handleGoogle}
            className="w-full h-12 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-md flex items-center justify-center gap-3 mb-5 transition-colors"
          >
            <svg className="w-5 h-5" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.6-6 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.6 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35 26.7 36 24 36c-5.3 0-9.7-3.4-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.7l6.2 5.2c-.4.4 6.6-4.8 6.6-14.9 0-1.3-.1-2.3-.4-3.5z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-purple-700/60" />
            <span className="text-purple-200 text-xs">or login with email</span>
            <div className="flex-1 h-px bg-purple-700/60" />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-white text-sm mb-2 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-300" />
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="pl-10 bg-white/95 border-0 h-12"
                />
              </div>
            </div>
            <div>
              <label className="text-white text-sm mb-2 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-purple-300" />
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-10 bg-white/95 border-0 h-12"
                />
              </div>
            </div>
            <Button type="submit" disabled={loading} className="w-full h-12 bg-fnl-pink hover:bg-fnl-pink text-white font-semibold btn-shadow-pink">
              {loading ? 'Signing in...' : 'Log In'}
            </Button>
          </form>
          <div className="text-center mt-4">
            <Link to="/forgot-password" className="text-purple-300 text-sm hover:text-fnl-pink" data-testid="login-forgot-link">
              Forgot your password?
            </Link>
          </div>
          <p className="text-center text-purple-200 text-sm mt-6">
            New here? <Link to="/" className="text-fnl-pink hover:underline font-semibold">Sign up for free</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
